/**
 * 今日の過ごし方の好み: 質問の選択肢と、答えから作る文章
 */

import type {
  DayStyleChoices,
  DayStyleExercise,
  DayStyleExerciseTime,
  DayStyleFocus,
  DayStyleRelax,
} from '@/api/types/sleep'

export type Option<T extends string> = { value: T; label: string }

export const DAY_STYLE_NOTE_MAX = 500

export const FOCUS_OPTIONS: Option<DayStyleFocus>[] = [
  { value: 'work', label: '仕事' },
  { value: 'study', label: '勉強' },
  { value: 'exam', label: '資格の勉強' },
  { value: 'creative', label: '創作' },
  { value: 'hard_reading', label: '難しい本' },
]

export const RELAX_OPTIONS: Option<DayStyleRelax>[] = [
  { value: 'reading', label: '読書' },
  { value: 'video', label: '動画' },
  { value: 'game', label: 'ゲーム' },
  { value: 'music', label: '音楽' },
  { value: 'bath', label: 'お風呂' },
  { value: 'stretch', label: 'ストレッチ' },
]

export const EXERCISE_OPTIONS: Option<DayStyleExercise>[] = [
  { value: 'gym', label: 'ジムで筋トレ' },
  { value: 'home', label: '家で筋トレ' },
  { value: 'running', label: 'ランニング' },
  { value: 'yoga', label: 'ヨガ' },
  { value: 'walk', label: '散歩だけ' },
  { value: 'none', label: 'しない' },
]

export const EXERCISE_TIME_OPTIONS: (Option<DayStyleExerciseTime> & { hours: string; about: string })[] = [
  {
    value: 'morning',
    label: '朝',
    hours: '6〜10時',
    about:
      '起きてすぐは腰（椎間板）に負担がかかりやすいので、起きて 1 時間ほどは前かがみの重い動きを避け、準備運動を長めに。朝の光を浴びると体内時計が整い、習慣にもしやすい',
  },
  {
    value: 'noon',
    label: '昼',
    hours: '11〜14時',
    about: '体が温まって動きやすい。食事は運動の 2 時間前までに済ませておくと動きやすい',
  },
  {
    value: 'evening',
    label: '夕方',
    hours: '16〜19時',
    about: '一日の中で一番力が出やすい（16〜20 時ごろが高い）。ただし続けたときの筋力・筋肉の伸びは朝と変わらない',
  },
  {
    value: 'night',
    label: '夜',
    hours: '19〜22時',
    about: '仕事の後で続けやすい。夜の運動で眠りが悪くなることはほとんどないが、激しい運動は寝る 1 時間前までに終える',
  },
]

/** 時間帯によらない運動のタイミングのコツ */
export const EXERCISE_TIMING_TIPS = [
  '頭をたくさん使った直後は筋トレの回数が落ちやすいので、深い集中の後は 1 時間ほど空けて運動の時間を取ります',
  '有酸素運動と筋トレを同じ日にするなら、6 時間ほど空けると筋トレの効果を邪魔しにくい',
  '続けたときの効果は朝も夕方も変わらないので、続けやすい時間を選ぶのがおすすめです',
]

export const labelsOf = <T extends string>(options: Option<T>[], values: T[]) =>
  options.filter((o) => values.includes(o.value)).map((o) => o.label)

const exerciseSentence = (a: DayStyleChoices) => {
  if (a.exercise.includes('none')) return '運動はしない。'
  const times = labelsOf(EXERCISE_TIME_OPTIONS, a.exercise_time)
  const when = times.length > 0 ? `${times.join('か')}に` : ''
  if (a.exercise.length > 0 && a.exercise.every((e) => e === 'walk')) return `運動は${when}散歩するくらい。`
  const kinds = labelsOf(
    EXERCISE_OPTIONS,
    a.exercise.filter((e) => e !== 'walk'),
  )
  if (kinds.length > 0) return `運動は${when}${kinds.join('や')}をしている。`
  if (times.length > 0) return `運動するなら${times.join('か')}がいい。`
  return null
}

/** 質問の答えから、AI に伝える文章を作る（答えていない質問は書かない） */
export const composeDayStyleText = (a: DayStyleChoices) => {
  const focus = labelsOf(FOCUS_OPTIONS, a.focus)
  const relax = labelsOf(RELAX_OPTIONS, a.relax)
  const reads = a.relax.includes('reading') || a.focus.includes('hard_reading')
  const lines = [
    focus.length > 0 ? `頭を使う時間は${focus.join('や')}に使いたい。` : null,
    relax.length > 0 ? `息抜きは${relax.join('や')}。` : null,
    (focus.length > 0 || relax.length > 0) && !reads ? '本はあまり読まない。' : null,
    exerciseSentence(a),
    a.nap === null ? null : a.nap ? '昼寝はする。' : '昼寝はしない。',
    a.review === null ? null : a.review ? '朝や寝る前に復習・暗記をしたい。' : '復習や暗記の時間はいらない。',
  ]
  return lines.filter((l) => l !== null).join('\n')
}

/** AI が読み取った内容を、画面に出す短い言葉の一覧にする */
export const summarizeChoices = (a: DayStyleChoices) => {
  const items: string[] = []
  const add = (title: string, labels: string[]) => {
    if (labels.length > 0) items.push(`${title}: ${labels.join('・')}`)
  }
  add('頭を使う', labelsOf(FOCUS_OPTIONS, a.focus))
  add('息抜き', labelsOf(RELAX_OPTIONS, a.relax))
  add('運動', labelsOf(EXERCISE_OPTIONS, a.exercise))
  add('運動の時間', labelsOf(EXERCISE_TIME_OPTIONS, a.exercise_time))
  if (a.nap !== null) items.push(a.nap ? '昼寝: する' : '昼寝: しない')
  if (a.review !== null) items.push(a.review ? '復習: する' : '復習: しない')
  return items
}
